import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { DomainHealthStory } from './stories/DomainHealthStory';

/**
 * Marketing page for the Domain Health add-on. Domain Health is the only
 * "plan-bundled" feature in the set — no slider, in-house DNS work, just a
 * fair-share credit weight (1 cr / lookup by default). The included-by-tier
 * strip splits into Trial vs Pro instead of Trial / Pro / Slider. SEO-optimised:
 * title pattern + structured FAQ block let Google surface it for "SPF DKIM
 * DMARC checker" searches.
 */
const CONTENT: FeatureContent = {
  kind: 'domain_health',
  slug: 'domain-health',
  story: <DomainHealthStory />,
  title: 'Domain Health',
  tagline: 'SPF · DKIM · DMARC · MX — scored, cached, surfaced on every record.',
  metaDescription:
    'Continuous SPF, DKIM, DMARC and MX checks per sending domain — a composite 0–100 health score badges every record, with drift alerts and fix recommendations.',
  eyebrow: 'Pro plans · core feature',
  description:
    "In-house DNS authentication checker. Every domain that touches your workspace — sender or recipient — gets a 0–100 score that lands as a per-record badge so weak senders surface before they cause deliverability damage. DNS lookups run against our own resolver — the per-domain credit weight is a fair-share guard against runaway use, not a pass-through cost.",
  icon: <Icon.ShieldCheck width={48} height={48} />,
  accent: 'accent',
  heroBg: '/heroes/feat-domain-health.webp',
  pillars: [
    {
      title: 'Full authentication posture',
      body: 'SPF record + policy qualifier, DKIM selectors discovered automatically, DMARC status + pct enforcement, MX hosts ranked by priority — all in one record per domain.',
    },
    {
      title: 'Per-record badge',
      body: 'The 0–100 score lands on every email record in your console + records browser. Spot mismatched / unprotected senders the moment they show up in a list, not weeks later.',
    },
    {
      title: 'TTL-respecting cache',
      body: 'One DNS round-trip per domain per 24 hours (or shorter, if their TTL is). 10,000 @gmail.com signups share one lookup — costs scale with unique domains, not row volume.',
    },
  ],
  steps: [
    {
      title: 'Ingest emails',
      body: 'Through the Console paste box, CSV upload, or the public detection API. Every new domain triggers a background DNS check.',
    },
    {
      title: 'We score it',
      body: 'SPF + DKIM + DMARC each contribute up to ~33 pts; DMARC reject + SPF -all caps out the upper band. Score is cached on the domain row.',
    },
    {
      title: 'Badge surfaces everywhere',
      body: 'On the record detail page, the records browser, the cluster card, the inbox-placement test detail — anywhere a domain shows up.',
    },
    {
      title: 'Drill in when needed',
      body: '/app/domains/health/:domain shows the raw SPF / DMARC TXT records, DKIM selectors, MX list, score breakdown, expiry timestamp — and a Refresh button.',
    },
  ],
  included: [
    {
      scope: 'trial',
      items: [
        'Full Domain Health for every domain you ingest',
        'Per-record badge surfaces immediately',
        'Auto-warm on ingest (opt-in)',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Everything in Trial',
        'Each fresh lookup draws the credit weight shown in the Pricing section above from your monthly wallet — cached results within TTL are free',
        'Pro plans grant enough monthly credits that typical workloads never notice the weight',
        'API access via /v1/domain-health (key-auth)',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Top up with a credit pack when your monthly allowance runs low',
        'Same per-lookup credit weight whether credits come from the plan or a pack',
        'Cache TTL means re-checking the same domain within 24h is always free',
      ],
    },
  ],
  faq: [
    {
      q: 'How are Domain Health lookups priced?',
      a: 'DNS lookups run in-house against our own resolver — no external pass-through cost. The credit weight shown in the Pricing section above is a fair-share guard against runaway use: each fresh lookup consumes it, cache hits within the 24h TTL are free. Pro plans grant enough monthly credits to cover tens of thousands of fresh lookups, so for most workspaces the weight is invisible in practice.',
    },
    {
      q: 'How fresh is the score?',
      a: "The lookup is cached for the shortest TTL we saw, capped at 24 hours. If a domain's SPF record changes, the score refreshes within that window. You can also force a refresh from /app/domains/health/:domain.",
    },
    {
      q: 'Can I bulk-check a list of domains?',
      a: 'Yes — POST a comma-separated list to GET /v1/domain-health/bulk?domains=a.com,b.com,…. Returns cached snapshots only (no refresh).',
    },
    {
      q: 'How does it interact with Inbox Placement and Warmup?',
      a: "Inbox Placement uses the sender's Domain Health snapshot to enrich the placement test detail (so a poor SPF/DMARC posture shows up next to a poor inbox-share). Warmup shows both our own score and an independent second domain check side-by-side on the per-inbox detail page so you can cross-reference both reads.",
    },
  ],
  // DH is now wallet-metered at 1 credit per lookup — surfaces the WalletPricingCard
  // with that weight. No slider ladder because DH was never sold as a per-feature
  // slider — it was plan-bundled only.
  creditFeature: 'domainHealth',
  relatedFeatures: [
    {
      slug: 'inbox-placement',
      title: 'Inbox Placement',
      tagline: 'See where your mail lands across Gmail / Yahoo / Outlook.',
    },
    {
      slug: 'blacklist-checks',
      title: 'Blacklist Checks',
      tagline: 'Blacklist aggregation runs on top of the same domain rows.',
    },
    {
      slug: 'warmup',
      title: 'Warmup',
      tagline: 'Build mailbox-level reputation alongside domain-level posture.',
    },
  ],
};

export function DomainHealthFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default DomainHealthFeaturePage;
